import stripHtmlEntities from "./strip-html-entities";

/** Repeatedly unwrap paired tags so nesting like `<strong><b>x</b></strong>` works. */
const unwrap = (
  html: string,
  tag: string,
  fn: (inner: string) => string,
): string => {
  const re = new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)<\\/${tag}>`, "gi");
  let out = html;
  let prev: string;
  do {
    prev = out;
    out = prev.replace(re, (_, inner: string) => fn(inner));
  } while (out !== prev);
  return out;
};

/**
 * Simple implementation that:
 * - replaces text tags (span, p, etc.) with plain text
 * - replaces h1, h2, h3, h4, h5, h6 tags with markdown heading syntax
 * - replaces br tags with new lines
 * - replaces strong tags with bold text
 * - replaces em tags with italic text
 * - replaces link tags with markdown link syntax
 * - replaces ul tags with markdown unordered list syntax
 * - replaces ol tags with markdown ordered list syntax
 * - replaces li tags with markdown list item syntax
 * - strips other tags
 */
const convertHtmlToMarkdown = (text: string): string => {
  let s = text.trim();

  // Replace br tags with new lines
  s = s.replace(/<br\s*\/?>/gi, "\n");

  for (let level = 6; level >= 1; level -= 1) {
    const h = `h${level}`;
    const hashes = "#".repeat(level);
    s = unwrap(s, h, (inner) => `\n${hashes} ${inner.trim()}\n\n`);
  }

  // Replace ol tags with markdown ordered list syntax
  s = s.replace(/<ol\b[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner: string) => {
    let n = 0;
    const body = inner.replace(
      /<li\b[^>]*>([\s\S]*?)<\/li>/gi,
      (_m, li: string) => {
        n += 1;
        return `\n${n}. ${li.trim()}`;
      },
    );
    return `\n${body.trim()}\n`;
  });

  // Replace ul tags with markdown unordered list syntax
  s = s.replace(/<ul\b[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner: string) => {
    const body = inner.replace(
      /<li\b[^>]*>([\s\S]*?)<\/li>/gi,
      (_m, li: string) => `\n- ${li.trim()}`,
    );
    return `\n${body.trim()}\n`;
  });

  // Replace strong tags with bold text
  for (const tag of ["strong", "b"] as const) {
    s = unwrap(s, tag, (inner) => `**${inner}**`);
  }

  // Replace em tags with italic text
  for (const tag of ["em", "i"] as const) {
    s = unwrap(s, tag, (inner) => `*${inner}*`);
  }

  // Replace link tags with markdown link syntax
  s = s.replace(
    /<a\b[^>]*href\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi,
    (_m, _q: string, href: string, inner: string) =>
      `[${inner.trim()}](${href.trim()})`,
  );

  // Strip text tags
  s = s.replace(
    /<\/?(?:p|span|div|section|article|header|footer|main|nav)\b[^>]*>/gi,
    "",
  );

  // Strip all other tags
  s = s.replace(/<[^>]+>/g, "");

  s = stripHtmlEntities(s);

  // Replace multiple spaces with a single space
  // Replace multiple new lines with a single new line
  // Trim the result
  s = s
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return s;
};

export default convertHtmlToMarkdown;
